import { logDebug, logError, logWarning } from "../utils/logger";
import Video from "./Video";
import { QUALITIES, Quality } from "@scrolly/renderer";
import { wirksameDpr } from "../utils/kartenSkala";

const IS_HTML_MODE = typeof document === "undefined";

const MOBILE_WIDTH = 850;

const CAMERA_STARTING_POSITION = {
    fov: 10, // the lower the FOV, the smaller the distortion
    far: 50000,
    x: 0,
    y: 0,
    z: 4000,
};

export default class RendererStore {
    static MAX_ASPECT = 2;
    static MIN_ASPECT = 0.5;

    static OLD_ASPECT = 16 / 9;

    static SLIDE_HEIGHT = 4096;
    static SLIDE_WIDTH = 4096;
    static SLIDE_DEPTH = 2048;

    static SLIDE_HEIGHT_OLD = 2160;
    static SLIDE_WIDTH_OLD = 3840;
    static SLIDE_DEPTH_OLD = 2160;

    dpr: number = !IS_HTML_MODE ? window.devicePixelRatio : 1;
    fov: number | undefined = undefined;
    far: number | undefined = undefined;
    antialiasing = false;
    useOrbitControls = false;
    cameraLookAt: Vector3Tuple | undefined = undefined;
    cameraPosition: Vector3Tuple | undefined = undefined;
    width = 1;
    height = 1;
    useXScaling = false;
    useYScaling = false;
    showLights = false;
    // GLOBAL-Tab "Camera" (Jans Original): Frustum-Drahtgitter + Kamera-Gizmo
    showCamera = false;
    landscape = false;
    aspectRatio = {
        name: "Auto",
        ratio: null,
    };

    viewportTop = 0;
    viewportLeft = 0;
    viewportWidth = 1;
    viewportHeight = 1;
    // boundsLerpInterpolated: Function | null = null

    /**
     * In the editor we expect things to work differently because e.g.
     * layers change and come and go
     */
    _usedInEditor = false;
    layers: Array<string> = [];
    loadedLayers: Array<string> = [];
    finishedLoading = false;

    useVideoPlayWorkaround = false;
    videosThatShouldBePlaying = new Set<Video>();

    /**
     * Zuletzt gewaehltes zentriertes Video (Ton-Serialisierung). Dient nur der
     * HYSTERESE: ohne sie kann der Gewinner bei zwei etwa gleich weit von der
     * Bildmitte entfernten Videos jeden Frame wechseln — der Ton springt dann
     * hin und her. Bisher fiel das nicht auf, weil durch den Einheiten-Fehler
     * in checkForOnScreen ohnehin nur EIN Video ueberhaupt in Frage kam.
     */
    aktivesVideoId: string | null = null;

    // Ton-Serialisierung fuer Pan-Videos: layer.id → {Abstand zur Bildmitte in px,
    // Zeitstempel}. Pro Frame in renderLayer aktualisiert; das Video mit dem
    // kleinsten frischen Abstand ist das "aktive" (zentrierte) und darf als
    // einziges abspielen+toenen. Frische-Fenster (Zeitstempel) verwirft Alt-
    // Eintraege verschwundener Layer / vorheriger Scrollys.
    videoCenterScores = new Map<
        string,
        // sichtbar: Deckkraft > 0 (rueckgeschrieben vom Hoerbarkeits-Block,
        // 1 Frame Latenz); undefined = noch nicht gemessen.
        // stapel: Position im Geschwister-Stapel (hoeher = weiter oben
        // gezeichnet) — trennt deckungsgleich gestapelte Template-Videos.
        {
            d: number;
            t: number;
            z: number;
            stumm: boolean;
            sichtbar?: boolean;
            /** Wirksame Deckkraft (0..1) — entscheidet die Ton-Uebergabe in
             *  Ueberblendungen, bevor der Stapel-Index greift (26.08.2026). */
            deckkraft?: number;
            stapel?: number;
            // false = Video hat eine Heimat-Slide (templateSlide) und der
            // Timer steht ausserhalb ihres Fensters; undefined = kein
            // templateSlide (natives Pro-Editor-Video) — Regel greift nicht.
            imFenster?: boolean;
        }
    >();

    /**
     * Das EINE hörbare Video (Jans Regel 24.08.2026): das zentralste, bei
     * nahezu gleichem Mittenabstand das vorderste (kleinste Kameradistanz z).
     * Auswahl in RendererWebGL (Ton-Auswahl-Block), gilt in allen Modi und
     * ist bewusst GETRENNT von aktivesVideoId (Abspiel-Gerüst des
     * once-Modus — daran sind #389/#393/#395 gescheitert).
     */
    tonVideoId: string | null = null;

    useOutlines = false;

    resizeFunction: (() => void) | undefined = undefined;

    numberOfFrames = 0;
    activatedStats = false;
    startDate: Date = new Date();
    endDate: Date = new Date();

    onProgress: (progress: number) => void;

    /**
     * Rueckrufe fuers Ladeende (finishedLoading kippt auf true) — unabhaengig
     * von der Render-Schleife, die in verdeckten Tabs (rAF tot) nicht laeuft.
     * Genutzt vom Reader, um die beim Boot zurueckgehaltenen Videos
     * freizugeben, sobald das Startvideo abspielbereit ist.
     */
    private ladeEndeRueckrufe: Array<() => void> = [];

    /** Rueckruf fuers Ladeende anmelden; ist der Boot schon durch, sofort ausfuehren. */
    beiLadeEnde(rueckruf: () => void) {
        if (this.finishedLoading) {
            rueckruf();
            return;
        }
        this.ladeEndeRueckrufe.push(rueckruf);
    }

    constructor(onProgress: (progress: number) => void) {
        this.cameraLookAt = [0, 0, 0];
        this.resetCamera();

        this.setAntialiasing = this.setAntialiasing.bind(this);
        this.setHeight = this.setHeight.bind(this);
        this.setWidth = this.setWidth.bind(this);
        this.setUseXScaling = this.setUseXScaling.bind(this);
        this.setUseYScaling = this.setUseYScaling.bind(this);
        this.toggleOrbitControls = this.toggleOrbitControls.bind(this);
        this.toggleShowLights = this.toggleShowLights.bind(this);
        this.toggleOutlines = this.toggleOutlines.bind(this);
        this.resetCamera = this.resetCamera.bind(this);
        this.onProgress = onProgress;
    }

    setResizeFunction(resizeFunction: TODOAny) {
        this.resizeFunction = resizeFunction;
    }

    setAspecRatio(aspectRatio: TODOAny) {
        this.aspectRatio = aspectRatio;
    }

    toggleLandscape() {
        this.landscape = !this.landscape;
    }

    setUseXScaling(useIt: boolean) {
        this.useXScaling = useIt;
    }

    setAntialiasing(antialiasing: boolean) {
        this.antialiasing = antialiasing;
    }

    setUseYScaling(useIt: boolean) {
        this.useYScaling = useIt;
    }

    setWidth(width: TODOAny, setViewport = true) {
        this.width = width;
        if (setViewport) {
            this.viewportWidth = width;
        }
    }

    setHeight(height: TODOAny, setViewport = true) {
        this.height = height;
        if (setViewport) {
            this.viewportHeight = height;
        }
    }

    setBounds(height: number, width: number, top: number, left: number) {
        this.viewportHeight = height;
        this.viewportWidth = width;
        this.viewportTop = top;
        this.viewportLeft = left;
    }

    get useMobile() {
        return this.viewportWidth < MOBILE_WIDTH;
    }

    get quality() {
        // vergroesserte Karte (utils/kartenSkala): Qualitaet nach sichtbarer Groesse
        const currentSize = Math.max(this.width, this.height) * wirksameDpr(this.dpr);
        const qualityMin = Math.min(1, currentSize / RendererStore.SLIDE_WIDTH);
        // check if it's a phone, then the maxqality should be 1536
        const isPhone = this.width < 500 || this.height < 500;
        const qualityMaxKey = isPhone ? Quality.Q768 : Quality.Q4096;
        const qualityMax = QUALITIES.find((q) => q.quality === qualityMaxKey);
        const index = QUALITIES.findIndex((p) => p.fraction >= qualityMin);
        // 1 will only slighly reduce the quality, but improve performance and load times.
        // only reduce if the choosen currently for high dpi screens
        let choosenQuality = QUALITIES[index];
        if (choosenQuality.quality === Quality.Q3072 || choosenQuality.quality === Quality.Q4096) {
            choosenQuality = QUALITIES[index - 1];
        }
        return choosenQuality.fraction > qualityMax!.fraction ? qualityMax : choosenQuality;
    }

    resetCamera() {
        this.fov = CAMERA_STARTING_POSITION.fov;
        this.far = CAMERA_STARTING_POSITION.far;
        this.cameraPosition = [CAMERA_STARTING_POSITION.x, CAMERA_STARTING_POSITION.y, CAMERA_STARTING_POSITION.z];
        this.useOrbitControls = false;
    }

    startFunction = () => {
        logWarning("Called startFunction before setStartFunction");
    };

    setStartFunction = (startFunction: TODOAny) => {
        this.startFunction = () => {
            startFunction();
            this.startFunction = () => {};
        };
    };

    /**
     * Wiedergabe vom Nutzer gestartet (START-Klick im Cover)? Gate für die
     * Video-HÖRBARKEIT (s. videoAudible.playerStarted): vor dem Start läuft
     * ein Cover-Video sichtbar, aber stumm. Gesetzt von ScrollyViewer.onStart
     * über RendererWebGL.setPlaybackStarted. Instanz-gebunden — pro
     * Viewer-Initialisierung frisch (kein globales Static, s. WebGLAssets-
     * Lektion: geteilte Statics brachen den Zwei-Viewer-Fall).
     */
    playbackStarted = false;

    /**
     * Gesten-Priming (25.08.2026, WK 'Bremerin' schwarze Videos): Safari
     * erlaubt play() nur PRO VIDEOELEMENT, das waehrend einer echten
     * Nutzer-Geste angestossen wurde. Der START-Klick primt darum ALLE
     * Videoelemente (stumm anspielen+pausieren) — sonst verweigert der
     * Browser spaeteren Videos das Anlaufen (nur das erste spielte;
     * Lauras iPhone-Befund im WK-Bericht). Gesetzt vom RendererWebGL,
     * aufgerufen im Klick-Handler des Viewers.
     */
    primeVideos: (() => void) | null = null;

    setPlaybackStarted(flag: boolean) {
        this.playbackStarted = flag;
    }

    isUsedInEditor() {
        return this._usedInEditor;
    }

    setUsedInEditor(flag: boolean) {
        this._usedInEditor = flag;
    }

    /**
     * BOOT-WATCHDOG (Jans Auftrag 24.08.2026, WK-Hänger „3D-Engine wird
     * gestartet … 70 %"): Ein Layer, dessen Asset weder lädt noch fehlschlägt
     * — gemessen: kalte imgproxy-Transforms (template-assets) hängen 20 s+ —
     * hielt den Boot unbegrenzt fest, weil `progress` nie 1 erreichte. Nach
     * Ablauf der Frist werden ausstehende Layer als geladen GEZÄHLT (der Boot
     * gibt frei), die echten Lade-Callbacks laufen im Hintergrund weiter und
     * tragen ihr Material nach — `loadedLayer` ist idempotent, ein spätes
     * Fertig-Signal zählt nicht doppelt.
     */
    static BOOT_LAYER_TIMEOUT_MS = 10_000;
    private bootWatchdog: ReturnType<typeof setTimeout> | null = null;

    private clearBootWatchdog() {
        if (this.bootWatchdog !== null) {
            clearTimeout(this.bootWatchdog);
            this.bootWatchdog = null;
        }
    }

    private armBootWatchdog() {
        this.clearBootWatchdog();
        // Editor hat keinen Fortschritts-Boot; leere Liste hat nichts abzuwarten.
        if (this.isUsedInEditor() || this.layers.length === 0) return;
        this.bootWatchdog = setTimeout(() => {
            this.bootWatchdog = null;
            const ausstehend = this.layers.filter((id) => !this.loadedLayers.includes(id));
            if (ausstehend.length === 0) return;
            logWarning(
                `Boot-Watchdog: ${ausstehend.length} Layer nach ${RendererStore.BOOT_LAYER_TIMEOUT_MS} ms ` +
                    `nicht fertig — Boot wird freigegeben, Assets laden nach. Layer: ${ausstehend.join(", ")}`
            );
            for (const id of ausstehend) {
                this.loadedLayer(id, "boot-watchdog-timeout");
            }
        }, RendererStore.BOOT_LAYER_TIMEOUT_MS);
    }

    setLayers(layers: Array<string>) {
        this.layers = Array.from(new Set(layers));
        this.armBootWatchdog();
    }

    loadedLayer(layerId: string, url: string) {
        // Idempotent (2026-08-04): denselben Layer nie doppelt zaehlen. Vorher lief
        // die Duplikat-Pruefung nur `if (!isUsedInEditor())` — im EDITOR wurde ein
        // Layer dadurch doppelt in loadedLayers gepusht (Fortschritt/State
        // verfaelscht), im Viewer als Fehler geloggt (Konsolen-Rauschen). Doppel-
        // Meldungen entstehen LEGITIM, wenn ein Layer neu verarbeitet wird (z.B.
        // waechst der troika-SDF-Atlas fuer einen komplexen Custom-OTF-Font, und
        // Text-Layer melden erneut "geladen"). In JEDEM Modus still zurueck.
        if (this.loadedLayers.includes(layerId)) return;
        if (!this.isUsedInEditor()) {
            if (!this.layers.includes(layerId)) {
                logError(new Error(`Tried to set unknown layer as loaded: ${layerId}`));
                return;
            }
        }

        logDebug(`LOADED layer ${layerId} ${url}`);
        this.loadedLayers.push(layerId);
        if (this.loadedLayers.length >= this.layers.length && !this.finishedLoading) {
            logDebug("finished loading");
            this.finishedLoading = true;
            this.clearBootWatchdog();
            this.armAufwaermWatchdog();
            const rueckrufe = this.ladeEndeRueckrufe;
            this.ladeEndeRueckrufe = [];
            rueckrufe.forEach((r) => {
                try {
                    r();
                } catch (e) {
                    logError(e as Error);
                }
            });
        }
        this.onProgress(this.progress);
    }

    resetLayers() {
        this.clearBootWatchdog();
        this.clearAufwaermWatchdog();
        this.onProgress(0);
        this.layers = [];
        this.loadedLayers = [];
        this.finishedLoading = false;
        this.aufwaermenFertig = false;
    }

    /**
     * AUFWAERMEN VOR VOLLEM BALKEN (Jans Entscheidung 13.10.2026, 3D-Power-
     * Test): Nach dem Laden aller Ebenen kompiliert der Renderer Shader,
     * laedt Texturen und den Schrift-Atlas auf die GPU (animate). Lief das
     * erst NACH vollem Balken, fiel es bei sofortigem START in den Start-Flug
     * (Safari: 0,6–1,2 s Ruckeln). Darum steht der Balken bis dahin auf
     * AUFWAERM_ANTEIL und wird erst mit aufwaermenAbgeschlossen() voll.
     * Der Waechter gibt spaetestens nach AUFWAERM_TIMEOUT_MS frei — ohne
     * rAF (Tab im Hintergrund) oder ohne Renderer wuerde START sonst nie frei.
     */
    static AUFWAERM_ANTEIL = 0.95;
    static AUFWAERM_TIMEOUT_MS = 4_000;
    aufwaermenFertig = false;
    private aufwaermWatchdog: ReturnType<typeof setTimeout> | null = null;

    aufwaermenAbgeschlossen() {
        this.clearAufwaermWatchdog();
        if (this.aufwaermenFertig) return;
        this.aufwaermenFertig = true;
        if (this.finishedLoading && !this.isUsedInEditor()) this.onProgress(this.progress);
    }

    private clearAufwaermWatchdog() {
        if (this.aufwaermWatchdog !== null) {
            clearTimeout(this.aufwaermWatchdog);
            this.aufwaermWatchdog = null;
        }
    }

    private armAufwaermWatchdog() {
        this.clearAufwaermWatchdog();
        if (this.isUsedInEditor() || this.aufwaermenFertig) return;
        this.aufwaermWatchdog = setTimeout(() => {
            this.aufwaermWatchdog = null;
            if (!this.aufwaermenFertig) {
                logWarning(`Aufwaermen nach ${RendererStore.AUFWAERM_TIMEOUT_MS} ms nicht fertig — Balken wird freigegeben.`);
                this.aufwaermenAbgeschlossen();
            }
        }, RendererStore.AUFWAERM_TIMEOUT_MS);
    }

    toggleOrbitControls() {
        this.useOrbitControls = !this.useOrbitControls;
    }

    toggleShowLights() {
        this.showLights = !this.showLights;
    }

    toggleOutlines() {
        this.useOutlines = !this.useOutlines;
    }

    get progress() {
        if (this.isUsedInEditor()) {
            logError(new Error("progress is not implemented in editor mode"));
            return 1.0;
        }

        const value = this.layers.length > 0 ? this.loadedLayers.length / this.layers.length : 0;
        if (value > 1.0) {
            logError(new Error(`Reached loaded progress greater than 1.0: ${value}`));
        }
        if (!this.aufwaermenFertig) return Math.min(value, RendererStore.AUFWAERM_ANTEIL);
        return value;
    }

    static cameraAspect(width: TODOAny, height: TODOAny) {
        return width / height;
    }

    static visibleSize(fov: TODOAny, cameraPositionZ: TODOAny, cameraAspect: TODOAny) {
        // update height/width stuff
        // Here we calculate the visible height/width at z = 0,
        // The math behind that can be found in this stackoverflow post:
        // https://stackoverflow.com/questions/13350875/three-js-width-of-view/13351534#13351534
        const DEG2RAD = Math.PI / 180;
        const vFOV = DEG2RAD * fov; // convert vertical fov to radians

        const visibleHeight = 2 * Math.tan(vFOV / 2) * cameraPositionZ; // visible height
        const visibleWidth = visibleHeight * cameraAspect; // visible width
        return {
            height: visibleHeight,
            width: visibleWidth,
        };
    }

    calculateBounds() {
        // const zoom = this.zoom;
        const { viewportWidth } = this;
        const { viewportHeight } = this;
        // const zoomYOffset = viewportHeight - viewportHeight * zoom;
        // const zoomXOffset = this.viewportLeft * zoom + (viewportWidth - viewportWidth * zoom);

        const deviceAspect = viewportWidth / viewportHeight;

        // bound our aspect ratio from 0.5 to 2
        const closestPossibleAspect = Math.max(
            Math.min(deviceAspect, RendererStore.MAX_ASPECT),
            RendererStore.MIN_ASPECT
        );
        const useMaxWidth = closestPossibleAspect > 1.0;

        // the width / height we will actually use
        const width = useMaxWidth ? viewportWidth : viewportHeight * closestPossibleAspect;
        const height = useMaxWidth ? viewportWidth / closestPossibleAspect : viewportHeight;
        /// how much of the filler will we cut?
        const cutOffFillerX = useMaxWidth ? 0 : (1 - closestPossibleAspect) / RendererStore.MIN_ASPECT;
        // TODO: investigate more
        const cutOffFillerY = useMaxWidth ? (1 - viewportHeight / viewportWidth) / RendererStore.MIN_ASPECT : 0;

        // TODO: remove magic numbers
        const safeAreaSizeXRelative = 1 / (2 - cutOffFillerX);
        const safeAreaSizeYRelative = 1 / (2 - cutOffFillerY);

        const bounds = {
            newTop: 0,
            newLeft: 0,
            newHeight: height,
            newWidth: width,
        };

        return {
            bounds,
            cutOffFillerX,
            cutOffFillerY,
            closestPossibleAspect,
            deviceAspect,
            useMaxWidth,
            safeAreaSizeXRelative,
            safeAreaSizeYRelative,
        };
    }

    collectStats() {
        if (this.activatedStats) {
            this.numberOfFrames += 1;
        }
    }

    resetStats() {
        this.numberOfFrames = 0;
        const newDate = new Date();
        this.startDate = newDate;
        this.endDate = newDate;
    }

    activateStats() {
        this.activatedStats = true;
    }

    deactivateStats() {
        this.activatedStats = false;
        this.endDate = new Date();
    }
}
